import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
    CalendarAbsenceDto,
    CalendarAppointmentDto,
    CalendarGridResponse,
} from '../../../../types/api-responses';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useToast } from '../../../../contexts/ToastContext';
import {
    computeStaffAvailability, isFreeAt, minToHhmm,
    type StaffAvailability,
} from '../../../../lib/staffAvailability';
import AppointmentCard, { AppointmentPeekPopover } from './AppointmentCard';
import {
    OWNER_STATUS_ORDER,
    bookingStatusColors,
    bookingStatusLabelKey,
    toLifecycleStatus,
    type LifecycleStatus,
} from '../../../../lib/bookingLifecycle';
import CurrentTimeLine from './CurrentTimeLine';
import s from './CalendarGrid.module.css';

/** Calendar-settings row that switches a lifecycle status on/off (Pending + Confirmed share "Booked"). */
function legendSettingsKey(status: LifecycleStatus): string {
    return status === 'Pending' || status === 'Approved' ? 'Booked' : status;
}

// AB#2015 — BE serialises absenceType as the enum NAME; map to an i18n key.
const ABSENCE_TYPE_KEY: Record<string, string> = {
    Break: 'staff.absence.type.break',
    SickLeave: 'staff.absence.type.sick',
    Vacation: 'staff.absence.type.vacation',
    PersonalTime: 'staff.absence.type.personal',
    Emergency: 'staff.absence.type.emergency',
};

// AB#2022 (CR-2026-07-19-v2) — colour-code availability. Whole-day leave
// (Vacation/Sick) reads as "off / can't book at all" in a RED family; a short
// intra-day break (Break/Personal/Emergency) reads as "on break here" in AMBER.
const LEAVE_TYPES = new Set(['Vacation', 'SickLeave']);
const isLeaveType = (t: string): boolean => LEAVE_TYPES.has(t);

interface CalendarGridProps {
    data: CalendarGridResponse;
    /** ISO yyyy-MM-dd the user is currently viewing — used to gate the current-time line. */
    viewedDate: string;
    /**
     * AB#1752 — Right-click handler for the staff column header. When set,
     * the grid wires `onContextMenu` on each staff header cell and bubbles
     * the (event, staff) pair up so the page can open a context menu
     * (Book-with / View schedule / Manage). When undefined, the native
     * browser context menu fires instead.
     */
    onStaffHeaderContextMenu?: (
        evt: React.MouseEvent,
        staff: { staffId: number; displayName: string; gender?: string | null },
    ) => void;
    /**
     * Click-to-create — owner clicks an empty area inside a staff column
     * to open the booking modal pre-filled with that exact (staff, date,
     * time). The grid derives the time from the click's y-coord relative
     * to dayStart and snaps to `slotGranularityMinutes` (defaults to 15).
     */
    onEmptyCellClick?: (
        staff: { staffId: number; displayName: string; gender?: string | null },
        date: string,
        timeHHmm: string,
    ) => void;
    /**
     * AB#2023 — "who's free at" check. When set ("HH:mm"), every staff column
     * resolves to free (normal) or blocked (dimmed + reason chip) at that time,
     * and the header pills evaluate at it. Null → pills evaluate at "now" when
     * viewing today, else show a day-level summary.
     */
    checkTime?: string | null;
    /** O1 (AB#2199) — branch name shown in the booking detail. */
    branchName?: string | null;
    /**
     * O1 (AB#2199, SO-09) — booking to open ONCE on arrival (deep link from the
     * Appointments list or a notification). The grid calls `onFocusConsumed` as
     * soon as it opened it, so the page clears it and a later remount (day / view
     * switch) never reopens the detail.
     */
    focusAppointmentId?: number | null;
    onFocusConsumed?: () => void;
    /** O1 (AB#2199, SO-09) — booking to outline briefly (the page clears it). */
    highlightAppointmentId?: number | null;
}

/** O1 (AB#2199) — column key for bookings with no staff column on this day. */
const UNASSIGNED_KEY = -1;

/** Local "yyyy-MM-dd" / "HH:mm" for the real current moment (timezone-safe). */
const p2 = (n: number) => String(n).padStart(2, '0');
const localToday = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
};
const nowHHmm = (): string => {
    const d = new Date();
    return `${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

/**
 * Time string "HH:mm" → minutes since midnight.
 */
function parseHHmm(t?: string | null): number {
    if (!t || !t.includes(':')) return 0;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return h * 60 + m;
}

/** "HH:mm" formatter. */
function formatHHmm(minutes: number): string {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Initial avatar text from a full name (first letters of first two words). */
function initials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map(p => p.charAt(0).toUpperCase()).join('');
}

/** Local-date string in yyyy-MM-dd. */
function localTodayIso(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * AB#1749 — Staff-as-columns day view grid.
 *
 * CSS Grid layout: time column (56px) + N staff columns (1fr each).
 * Time rows are drawn as horizontal rules at `slotGranularityMinutes`
 * intervals; the body is one tall track per staff so we can position
 * appointment cards absolutely against `top = (start - dayStart) * px/min`.
 * Pixels-per-minute = 1.4 — gives 84px per hour at 60min slot, scales
 * down to ~21px at 15min spacing (Dina-approved per design-handoff §3).
 */
const CalendarGrid: React.FC<CalendarGridProps> = ({
    data, viewedDate, onStaffHeaderContextMenu, onEmptyCellClick, checkTime,
    branchName, focusAppointmentId, onFocusConsumed, highlightAppointmentId,
}) => {
    const { t } = useLanguage();
    const toast = useToast();
    // O1 (AB#2199) — keep the open booking's ID, not a snapshot: after any status
    // change (here or elsewhere) the detail re-renders from the refreshed grid.
    const [openApptId, setOpenApptId] = useState<number | null>(null);
    const openAppt = openApptId == null ? null : data.appointments.find(a => a.id === openApptId) ?? null;
    const setOpenAppt = (a: CalendarAppointmentDto | null) => setOpenApptId(a?.id ?? null);

    // O1 (AB#2199, SO-09) — a deep link (Appointments list, notification) names a
    // booking: open its detail once the grid for its branch + day contains it,
    // then tell the page it was consumed.
    const onFocusConsumedRef = useRef(onFocusConsumed);
    onFocusConsumedRef.current = onFocusConsumed;
    useEffect(() => {
        if (focusAppointmentId == null) return;
        if (data.appointments.some(a => a.id === focusAppointmentId)) {
            setOpenApptId(focusAppointmentId);
            onFocusConsumedRef.current?.();
        }
    }, [focusAppointmentId, data.appointments]);

    // AB#2023 — reference time the availability pills evaluate at: the "who's
    // free at" value if set, else the real "now" when viewing today, else null
    // (future/past day → show a day-level summary, no live "now").
    const referenceTime = checkTime ?? (viewedDate === localToday() ? nowHHmm() : null);
    const availabilityFor = (staffId: number): StaffAvailability => computeStaffAvailability({
        shiftStart: data.staff.find(s => s.staffId === staffId)?.shiftStart,
        shiftEnd: data.staff.find(s => s.staffId === staffId)?.shiftEnd,
        absences: absencesByStaff.get(staffId) ?? [],
        appointments: apptsByStaff.get(staffId) ?? [],
        referenceTime,
    });

    // AB#2023 — pill label + tone for a staff availability state.
    const pillFor = (av: StaffAvailability): { cls: string; label: string } => {
        switch (av.kind) {
            case 'leave': return { cls: s.pillLeave, label: t('calendar.avail.onLeave') };
            case 'off': return { cls: s.pillOff, label: t('calendar.avail.off') };
            case 'break': return { cls: s.pillBreak, label: t('calendar.avail.onBreak').replace('{time}', `⁨${minToHhmm(av.backAtMin)}⁩`) };
            case 'booked': return {
                cls: s.pillBusy,
                label: av.nextFreeMin != null
                    ? `${t('calendar.avail.booked')} · ${t('calendar.avail.nextFree').replace('{time}', `⁨${minToHhmm(av.nextFreeMin)}⁩`)}`
                    : t('calendar.avail.booked'),
            };
            case 'offHours': return { cls: s.pillOff, label: t('calendar.avail.offHours') };
            case 'available': return { cls: s.pillFree, label: t('calendar.avail.available') };
            case 'working': return { cls: s.pillWorking, label: t('staff.status.working').replace('{range}', `⁨${minToHhmm(av.startMin)}–${minToHhmm(av.endMin)}⁩`) };
        }
    };

    const reasonChip = (reason: 'break' | 'leave' | 'booked' | 'off'): string =>
        t(`calendar.avail.reason.${reason}`);

    const dayStartMin = parseHHmm(data.dayStart);
    const dayEndMin = parseHHmm(data.dayEnd);
    const totalMinutes = Math.max(60, dayEndMin - dayStartMin);
    const PIXELS_PER_MINUTE = 1.4;
    const bodyHeight = totalMinutes * PIXELS_PER_MINUTE;

    const slot = data.slotGranularityMinutes || 15;

    // Time row labels — one per slot from dayStart → dayEnd inclusive.
    const timeRows = useMemo(() => {
        const rows: { minute: number; label: string; isHour: boolean }[] = [];
        for (let m = dayStartMin; m <= dayEndMin; m += slot) {
            rows.push({
                minute: m,
                label: formatHHmm(m),
                isHour: m % 60 === 0,
            });
        }
        return rows;
    }, [dayStartMin, dayEndMin, slot]);

    // Group appointments by staffId for column-local rendering. Unassigned
    // (staffId=null) appointments go into a virtual "Unassigned" column at
    // the end — keeps walk-ins-without-staff visible.
    //
    // O1 (AB#2199, SO-09) — that column was described here but never drawn, so
    // a request with no staff (most customer requests on DEV) — or one assigned
    // to a staff member who has no column on this branch/day — was invisible:
    // a deep link landed on the right day and showed nothing. Both now go to
    // the UNASSIGNED_KEY column rendered after the staff columns.
    const apptsByStaff = useMemo(() => {
        const map = new Map<number, CalendarAppointmentDto[]>();
        const columnIds = new Set(data.staff.map(st => st.staffId));
        for (const a of data.appointments) {
            const key = a.staffId != null && columnIds.has(a.staffId) ? a.staffId : UNASSIGNED_KEY;
            const arr = map.get(key) ?? [];
            arr.push(a);
            map.set(key, arr);
        }
        return map;
    }, [data.appointments, data.staff]);
    const unassignedAppts = apptsByStaff.get(UNASSIGNED_KEY) ?? [];
    const hasUnassigned = unassignedAppts.length > 0;

    // AB#2015 — group absences (breaks + leave) by staffId for column overlay.
    const absencesByStaff = useMemo(() => {
        const map = new Map<number, CalendarAbsenceDto[]>();
        for (const ab of data.absences ?? []) {
            const arr = map.get(ab.staffId) ?? [];
            arr.push(ab);
            map.set(ab.staffId, arr);
        }
        return map;
    }, [data.absences]);

    const isToday = viewedDate === localTodayIso();

    // Layer 2 (audit Phase B-2) — density-aware column sizing.
    //   N <= 6   → equal-share columns (1fr each). Cards are roomy.
    //   N = 7-10 → auto-compact: same equal-share but smaller card
    //              padding + font via `.gridRootCompact` class.
    //   N >= 11  → minmax(120px, 1fr): grid spills horizontally and
    //              the outer wrapper scrolls. Compact styling stays
    //              on so cards within the narrower columns remain
    //              legible.
    // Manual [Normal | Compact] toggle deferred until UAT tells us
    // the auto-pick is wrong for the founder's scale (most salons
    // have 3-8 staff so the auto-rule should fit).
    // Tracks = staff columns + the Unassigned column; the zero-staff placeholder
    // ("no staff at this branch") is drawn only when there is nothing unassigned,
    // so header and body always have exactly N tracks (review N1).
    const N = data.staff.length + (hasUnassigned ? 1 : 0);
    const isCompact = N >= 7;
    const isScrolling = N >= 11;
    const gridTemplateColumns = calendarGridTemplate(N);

    return (
        <>
            {/* Layer 2 — outer scroll wrapper kicks in only when N >= 11.
                For smaller staff counts the inner grid fits viewport. */}
            <div className={isScrolling ? s.gridScrollWrap : ''}>
            <div
                className={`${s.gridRoot} ${isCompact ? s.gridRootCompact : ''}`}
                style={{ display: 'grid', gridTemplateColumns }}
            >
                {/* Header row */}
                <div
                    className={s.headerRow}
                    style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns }}
                    data-testid="calendar-header-row"
                >
                    <div className={s.timeColHeader} />
                    {data.staff.length === 0 && !hasUnassigned ? (
                        <div className={s.staffHeaderCell}>
                            <span className={s.staffOff}>{t('calendar.noStaff')}</span>
                        </div>
                    ) : (
                        data.staff.map(st => (
                            <div
                                key={st.staffId}
                                className={s.staffHeaderCell}
                                onContextMenu={onStaffHeaderContextMenu
                                    ? (e) => {
                                        e.preventDefault();
                                        onStaffHeaderContextMenu(e, {
                                            staffId: st.staffId,
                                            displayName: st.displayName,
                                            gender: st.gender,
                                        });
                                    }
                                    : undefined
                                }
                                title={onStaffHeaderContextMenu ? t('cal.staff.openActions').replace('{name}', st.displayName) : undefined}
                            >
                                <div className={s.staffAvatar}>
                                    {st.photoUrl
                                        ? <img src={st.photoUrl} alt="" />
                                        : initials(st.displayName)}
                                </div>
                                <div className={s.staffMeta}>
                                    <span className={s.staffNameRow}>
                                        <span className={s.staffName}>
                                            {st.displayName}
                                            {st.gender === 'Female' && (
                                                <span className={s.genderGlyph} aria-label={t('cal.staff.female')}>♀</span>
                                            )}
                                            {st.gender === 'Male' && (
                                                <span className={s.genderGlyph} aria-label={t('cal.staff.male')}>♂</span>
                                            )}
                                        </span>
                                        {/* AB#1976 — dropdown affordance sits right beside
                                            the name. Left-click opens the same staff actions
                                            menu as right-click (add break, book, manage). */}
                                        {onStaffHeaderContextMenu && (
                                            <button
                                                type="button"
                                                className={s.staffCaret}
                                                aria-label={t('cal.staff.actions').replace('{name}', st.displayName)}
                                                aria-haspopup="menu"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onStaffHeaderContextMenu(e, {
                                                        staffId: st.staffId,
                                                        displayName: st.displayName,
                                                        gender: st.gender,
                                                    });
                                                }}
                                            >
                                                ▾
                                            </button>
                                        )}
                                    </span>
                                    {st.shiftStart && st.shiftEnd ? (
                                        <span className={s.staffShift}>
                                            {st.shiftStart}–{st.shiftEnd}
                                        </span>
                                    ) : (
                                        <span className={s.staffOff}>{t('cal.staff.off')}</span>
                                    )}
                                    {/* AB#2023 — availability pill: at-a-glance
                                        free / on break / on leave / next-free. */}
                                    {(() => {
                                        const { cls, label } = pillFor(availabilityFor(st.staffId));
                                        return <span className={`${s.availPill} ${cls}`}>{label}</span>;
                                    })()}
                                </div>
                            </div>
                        ))
                    )}
                    {hasUnassigned && (
                        <div className={s.staffHeaderCell} data-testid="unassigned-header">
                            <div className={s.staffMeta}>
                                <span className={s.staffName}>{t('calendar.unassigned')}</span>
                                <span className={s.staffShift}>{t('calendar.unassignedHint')}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Body row — time labels + staff columns */}
                <div
                    className={s.body}
                    style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns, position: 'relative' }}
                    data-testid="calendar-body-row"
                >
                    {/* Time labels column */}
                    <div style={{ position: 'relative', height: `${bodyHeight}px` }}>
                        {timeRows.map(r => (
                            <div
                                key={r.minute}
                                className={`${s.timeCell} ${r.isHour ? s.timeCellHour : ''}`}
                                style={{
                                    position: 'absolute',
                                    top: `${(r.minute - dayStartMin) * PIXELS_PER_MINUTE}px`,
                                    insetInlineStart: 0,
                                    insetInlineEnd: 0,
                                    height: `${slot * PIXELS_PER_MINUTE}px`,
                                }}
                            >
                                {r.label}
                            </div>
                        ))}
                    </div>

                    {/* Staff columns — render N columns even when zero staff
                        so the placeholder appears in a single full-width cell. */}
                    {data.staff.length === 0 && !hasUnassigned ? (
                        <div
                            className={s.staffColumn}
                            style={{ position: 'relative', height: `${bodyHeight}px` }}
                        >
                            {timeRows.map(r => (
                                <div
                                    key={r.minute}
                                    className={`${s.rowDivider} ${r.isHour ? s.rowDividerHour : ''}`}
                                    style={{ top: `${(r.minute - dayStartMin) * PIXELS_PER_MINUTE}px` }}
                                />
                            ))}
                            <div className={s.offOverlay} />
                        </div>
                    ) : (
                        data.staff.map(st => {
                            const appts = apptsByStaff.get(st.staffId) ?? [];
                            const isOff = !st.shiftStart || !st.shiftEnd;
                            // AB#2023 — "who's free at" check: dim + reason-chip
                            // the columns that can't take a booking at that time.
                            const free = checkTime
                                ? isFreeAt({
                                    shiftStart: st.shiftStart, shiftEnd: st.shiftEnd,
                                    absences: absencesByStaff.get(st.staffId) ?? [],
                                    appointments: appts, referenceTime: checkTime,
                                })
                                : null;
                            // Click-to-create handler — derive (date, time) from
                            // the click's y-coord relative to the staff column
                            // and snap to the slot granularity. We bind the
                            // handler on OFF-DAYS too (owners override shifts
                            // for walk-ins / schedule exceptions); the BE will
                            // return Slot.CONFLICT if the time genuinely isn't
                            // bookable, and the modal drops to the slot picker
                            // so the owner sees what IS free.
                            const onColClick = onEmptyCellClick
                                ? (e: React.MouseEvent<HTMLDivElement>) => {
                                    // Skip if the click landed on an existing
                                    // appointment card (those have their own
                                    // onClick → peek popover). CSS.escape() on
                                    // the CSS-module hash is a defense against
                                    // webpack producing hashes that contain CSS
                                    // combinator characters (`+`, `~`, `>`,
                                    // etc.) which silently break `closest()` —
                                    // exactly the regression that took WeekView
                                    // clicks offline (its `weekCell` hash drew
                                    // a `+`, the unescaped selector parsed as
                                    // adjacent-sibling, `closest()` returned
                                    // null, every click was suppressed).
                                    const target = e.target as HTMLElement;
                                    if (target.closest(`.${CSS.escape(s.staffColumn)}`) !== e.currentTarget) return;
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const y = e.clientY - rect.top;
                                    const rawMin = dayStartMin + (y / PIXELS_PER_MINUTE);
                                    const snapped = Math.floor(rawMin / slot) * slot;
                                    const clamped = Math.max(dayStartMin, Math.min(snapped, dayEndMin - slot));
                                    const time = formatHHmm(clamped);
                                    onEmptyCellClick(
                                        { staffId: st.staffId, displayName: st.displayName, gender: st.gender },
                                        data.date,
                                        time,
                                    );
                                }
                                : undefined;
                            return (
                                <div
                                    key={st.staffId}
                                    className={`${s.staffColumn} ${onColClick ? s.staffColumnClickable : ''}`
                                        + (free ? (free.free ? ` ${s.colFree}` : ` ${s.colBlocked}`) : '')}
                                    style={{ position: 'relative', height: `${bodyHeight}px` }}
                                    data-testid={`staff-column-${st.staffId}`}
                                    onClick={onColClick}
                                    title={onColClick ? t('cal.staff.clickToBook').replace('{name}', st.displayName) : undefined}
                                >
                                    {/* AB#2023 — free-at-time reason chip when a check time is active. */}
                                    {free && !free.free && free.reason && (
                                        <div className={s.blockedChip} aria-hidden="true">
                                            <span className={s.blockedDot} />
                                            {reasonChip(free.reason)}
                                        </div>
                                    )}
                                    {timeRows.map(r => (
                                        <div
                                            key={r.minute}
                                            className={`${s.rowDivider} ${r.isHour ? s.rowDividerHour : ''}`}
                                            style={{ top: `${(r.minute - dayStartMin) * PIXELS_PER_MINUTE}px` }}
                                        />
                                    ))}
                                    {isOff && <div className={s.offOverlay} aria-hidden="true" />}
                                    {/* AB#2015 / AB#2022 — staff absences as colour-coded,
                                        click-blocking blocks. RED = whole-day leave (off all
                                        day), AMBER = short break. Clicking one shows an
                                        "unavailable" notice instead of opening the booking
                                        modal (stopPropagation), so the owner can't book into
                                        it; the BE also gates booking at the engine layer. */}
                                    {(absencesByStaff.get(st.staffId) ?? []).map(ab => {
                                        const label = t(ABSENCE_TYPE_KEY[ab.absenceType] ?? 'staff.absence.type.break');
                                        const kindClass = isLeaveType(ab.absenceType) ? s.absenceLeave : s.absenceBreakKind;
                                        const blockBooking = (e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            toast.showToast(
                                                t('calendar.absence.unavailable').replace('{type}', label),
                                                'warning');
                                        };
                                        if (ab.isWholeDay || !ab.startTime || !ab.endTime) {
                                            return (
                                                <div
                                                    key={`ab-${ab.id}`}
                                                    className={`${s.absenceBlock} ${s.absenceWholeDay} ${kindClass}`}
                                                    title={label}
                                                    onClick={blockBooking}
                                                >
                                                    <span className={s.absenceLabel}>{label}</span>
                                                </div>
                                            );
                                        }
                                        const startM = parseHHmm(ab.startTime);
                                        const endM = parseHHmm(ab.endTime);
                                        const top = (startM - dayStartMin) * PIXELS_PER_MINUTE;
                                        const height = Math.max(14, (endM - startM) * PIXELS_PER_MINUTE);
                                        return (
                                            <div
                                                key={`ab-${ab.id}`}
                                                className={`${s.absenceBlock} ${kindClass}`}
                                                style={{ top: `${top}px`, height: `${height}px` }}
                                                title={`${label} ⁨${ab.startTime}–${ab.endTime}⁩${ab.reason ? ` · ${ab.reason}` : ''}`}
                                                onClick={blockBooking}
                                            >
                                                <span className={s.absenceLabel}>
                                                    {label}{ab.reason ? ` · ${ab.reason}` : ''}
                                                </span>
                                                {/* AB#2023 — show the break time on the
                                                    block itself, not just on hover. */}
                                                <span className={s.absenceTime}>
                                                    ⁨{ab.startTime}–{ab.endTime}⁩
                                                </span>
                                            </div>
                                        );
                                    })}
                                    {/* AB#2034 — bundle grouping. A customer package
                                        bundle's back-to-back appointments are wrapped
                                        by an accent spine + name chip so the owner reads
                                        them as one linked visit. The band sits BEHIND the
                                        cards (earlier in DOM + pointer-events:none) so each
                                        service card stays individually clickable. */}
                                    {computeBundleGroups(appts).map(g => {
                                        // Extend the bracket up by a header strip so the name
                                        // chip sits ABOVE the first card instead of overlapping
                                        // its content. Clamp to the grid top so a bundle at
                                        // day-start doesn't overflow above the body.
                                        const BUNDLE_HEADER_PX = 20;
                                        const cardsTop = (g.startMin - dayStartMin) * PIXELS_PER_MINUTE;
                                        const cardsBottom = (g.endMin - dayStartMin) * PIXELS_PER_MINUTE;
                                        const top = Math.max(0, cardsTop - BUNDLE_HEADER_PX);
                                        const height = Math.max(20, cardsBottom - top);
                                        // Bidi-isolate the owner-entered bundle name (may be
                                        // Latin or Arabic) — same FSI/PDI pattern the time
                                        // chips use. <bdi> on the visible chip; the aria
                                        // string carries the isolated name too.
                                        const nameForAria = g.name ?? t('bundle.tag');
                                        return (
                                            <div
                                                key={`bundle-${g.bundleBookingId}`}
                                                className={s.bundleBand}
                                                style={{ top: `${top}px`, height: `${height}px` }}
                                                role="group"
                                                aria-label={t('bundle.groupAria').replace('{name}', `⁨${nameForAria}⁩`)}
                                            >
                                                <span className={s.bundleChip} aria-hidden="true">
                                                    {g.name
                                                        ? <bdi>{g.name}</bdi>
                                                        : t('bundle.tag')}
                                                </span>
                                            </div>
                                        );
                                    })}
                                    {/* Layer 3 (audit Phase B-3) — within a
                                        staff column, compute lane assignment
                                        for overlapping appointments so they
                                        render side-by-side instead of stacking
                                        on top of each other. assignLanes is
                                        a pure helper at the bottom of this
                                        file; default lane=0/laneCount=1 means
                                        the common no-overlap case still renders
                                        full-column-width. */}
                                    {assignLanes(appts).map(({ appt, lane, laneCount }) => (
                                        <AppointmentCard
                                            key={appt.id}
                                            appointment={appt}
                                            statuses={data.statuses}
                                            dayStartMinutes={dayStartMin}
                                            pixelsPerMinute={PIXELS_PER_MINUTE}
                                            onOpen={setOpenAppt}
                                            lane={lane}
                                            laneCount={laneCount}
                                            highlighted={appt.id === highlightAppointmentId}
                                        />
                                    ))}
                                </div>
                            );
                        })
                    )}

                    {/* O1 (AB#2199, SO-09) — bookings without a staff column on this day. */}
                    {hasUnassigned && (
                        <div
                            className={s.staffColumn}
                            style={{ position: 'relative', height: `${bodyHeight}px` }}
                            data-testid="unassigned-column"
                        >
                            {timeRows.map(r => (
                                <div
                                    key={r.minute}
                                    className={`${s.rowDivider} ${r.isHour ? s.rowDividerHour : ''}`}
                                    style={{ top: `${(r.minute - dayStartMin) * PIXELS_PER_MINUTE}px` }}
                                />
                            ))}
                            {assignLanes(unassignedAppts).map(({ appt, lane, laneCount }) => (
                                <AppointmentCard
                                    key={appt.id}
                                    appointment={appt}
                                    statuses={data.statuses}
                                    dayStartMinutes={dayStartMin}
                                    pixelsPerMinute={PIXELS_PER_MINUTE}
                                    onOpen={setOpenAppt}
                                    lane={lane}
                                    laneCount={laneCount}
                                    highlighted={appt.id === highlightAppointmentId}
                                />
                            ))}
                        </div>
                    )}

                    {/* Current-time line — positioned absolutely against the body */}
                    <CurrentTimeLine
                        dayStartMinutes={dayStartMin}
                        dayEndMinutes={dayEndMin}
                        pixelsPerMinute={PIXELS_PER_MINUTE}
                        isToday={isToday}
                    />
                </div>
            </div>
            </div>{/* /Layer 2 scroll wrap */}

            {/* Status legend — O3 (SO-10/SO-11/SO-42): the owner lifecycle words and
                design-system colours, identical to the cards and the Appointments
                list. A status switched off in Calendar settings is hidden unless a
                booking on screen still carries it. */}
            <div className={s.legend} role="list" aria-label={t('cal.legend.label')}>
                {OWNER_STATUS_ORDER
                    .filter(st =>
                        data.statuses.find(r => r.statusKey === legendSettingsKey(st))?.enabled !== false
                        || data.appointments.some(a => toLifecycleStatus(a.status) === st))
                    .map(st => (
                    <span key={st} className={s.legendItem} role="listitem">
                        <span
                            className={s.legendDot}
                            style={{ background: bookingStatusColors(st).fg }}
                            aria-hidden="true"
                        />
                        {t(bookingStatusLabelKey(st))}
                    </span>
                ))}
                {/* AB#2022 — availability legend chips matching the absence blocks. */}
                <span className={s.legendItem} role="listitem">
                    <span className={`${s.legendDot} ${s.legendDotLeave}`} aria-hidden="true" />
                    {t('calendar.legend.onLeave')}
                </span>
                <span className={s.legendItem} role="listitem">
                    <span className={`${s.legendDot} ${s.legendDotBreak}`} aria-hidden="true" />
                    {t('calendar.legend.onBreak')}
                </span>
                <span className={s.legendMobileHint} aria-hidden="true">
                    {t('cal.legend.mobileHint')}
                </span>
            </div>

            {openAppt && (
                <AppointmentPeekPopover
                    appointment={openAppt}
                    statuses={data.statuses}
                    staffName={data.staff.find(st => st.staffId === openAppt.staffId)?.displayName ?? null}
                    branchName={branchName ?? null}
                    onClose={() => setOpenAppt(null)}
                />
            )}
        </>
    );
};

export default CalendarGrid;

/**
 * SO-01 (AB#2199) — the ONE column template shared by the header row and the
 * body. Every staff track has a fixed minimum (0, or 120px when the grid
 * scrolls) instead of `1fr` (= `minmax(auto, 1fr)`): with `auto` a long staff
 * name or availability pill grew its HEADER track while the body tracks stayed
 * equal, so cards were drawn under the wrong staff header with 4+ staff.
 * Exported for the alignment regression test.
 */
export function calendarGridTemplate(staffCount: number): string {
    return staffCount >= 11
        ? `56px repeat(${staffCount}, minmax(120px, 1fr))`
        : `56px repeat(${Math.max(1, staffCount)}, minmax(0, 1fr))`;
}

/* ─────────────────── Layer 3 (audit Phase B-3) ───────────────────── */

/**
 * Within a single staff column, lay overlapping appointments out
 * side-by-side instead of stacking them on top of each other (which
 * silently buried every card except the last-painted one — exactly
 * the same density-blindness as Week-view before Layer 1, just hidden
 * inside one column).
 *
 * Approach: greedy lane assignment.
 *
 *   1. Sort appointments by startTime ascending, then by endTime
 *      descending so longer appointments get lower lane indices —
 *      that gives a more stable visual when the cluster grows.
 *   2. For each appointment, find the lowest-indexed lane whose
 *      previously-assigned card ended at or before this card's
 *      start. If none exists, open a new lane.
 *   3. After lane assignment, compute laneCount per card as the
 *      MAX lane (+1) among all appointments that overlap with it.
 *      This ensures all cards in a cluster claim the same width
 *      so they line up cleanly; cards in disjoint clusters can
 *      have different laneCount values.
 *
 * Inputs/outputs are plain DTOs — no React, no DOM. Pure function,
 * easy to unit-test.
 */
export interface LaneAssignment {
    appt: CalendarAppointmentDto;
    lane: number;
    laneCount: number;
}

/* ─────────────────── AB#2034 — bundle grouping ───────────────────── */

/**
 * AB#2034 — a customer package bundle rendered as one linked set in a staff
 * column. A bundle's appointments are booked back-to-back for one staff, so the
 * group spans a single contiguous [startMin, endMin) block; the grid draws an
 * accent spine + a name chip over that span so the owner reads it as one visit
 * while each service card stays individually clickable.
 */
export interface BundleGroup {
    bundleBookingId: number;
    /** Bundle display name; null when the BE couldn't resolve it (falls back to the generic tag). */
    name: string | null;
    startMin: number;
    endMin: number;
    count: number;
}

/**
 * Group a staff column's appointments by bundleBookingId into contiguous spans.
 * Standalone appointments (no bundleBookingId) are ignored. Exported for unit tests.
 */
export function computeBundleGroups(appts: CalendarAppointmentDto[]): BundleGroup[] {
    const byId = new Map<number, CalendarAppointmentDto[]>();
    for (const a of appts) {
        if (a.bundleBookingId == null) continue;
        const arr = byId.get(a.bundleBookingId) ?? [];
        arr.push(a);
        byId.set(a.bundleBookingId, arr);
    }
    const groups: BundleGroup[] = [];
    byId.forEach((members, bundleBookingId) => {
        let startMin = Number.POSITIVE_INFINITY;
        let endMin = Number.NEGATIVE_INFINITY;
        let name: string | null = null;
        for (const m of members) {
            const st = parseHHmm(m.startTime);
            const en = parseHHmm(m.endTime);
            if (st < startMin) startMin = st;
            if (en > endMin) endMin = en;
            if (!name && m.bundleName) name = m.bundleName;
        }
        groups.push({ bundleBookingId, name, startMin, endMin, count: members.length });
    });
    // Stable order: earliest first.
    return groups.sort((a, b) => a.startMin - b.startMin);
}

/**
 * Exported for unit tests in CalendarGrid.assignLanes.test.ts.
 * Not part of the component's public API otherwise — callers within
 * this file consume it as a local closure.
 */
export function assignLanes(appts: CalendarAppointmentDto[]): LaneAssignment[] {
    if (appts.length === 0) return [];
    if (appts.length === 1) {
        return [{ appt: appts[0], lane: 0, laneCount: 1 }];
    }

    const sorted = [...appts].sort((a, b) => {
        if (a.startTime !== b.startTime) return a.startTime.localeCompare(b.startTime);
        return b.endTime.localeCompare(a.endTime);
    });

    const overlaps = (a: CalendarAppointmentDto, b: CalendarAppointmentDto) =>
        a.startTime < b.endTime && b.startTime < a.endTime;

    // Greedy lane assignment. laneEnds[i] = endTime of the most recently
    // assigned card in lane i.
    const laneEnds: string[] = [];
    const laneByIdx = sorted.map((cur) => {
        let assigned = -1;
        for (let j = 0; j < laneEnds.length; j++) {
            if (laneEnds[j] <= cur.startTime) {
                assigned = j;
                break;
            }
        }
        if (assigned === -1) {
            assigned = laneEnds.length;
            laneEnds.push(cur.endTime);
        } else {
            laneEnds[assigned] = cur.endTime;
        }
        return assigned;
    });

    // For each card, laneCount = max(lane+1) among overlapping siblings.
    // O(n²) but n is small (typical staff has < 20 appts/day).
    return sorted.map((cur, i) => {
        let maxLane = laneByIdx[i];
        for (let j = 0; j < sorted.length; j++) {
            if (i === j) continue;
            if (overlaps(cur, sorted[j])) {
                if (laneByIdx[j] > maxLane) maxLane = laneByIdx[j];
            }
        }
        return { appt: cur, lane: laneByIdx[i], laneCount: maxLane + 1 };
    });
}
